import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { DueDateType, DueDateTypeVersion, UpsertDueDateType } from "./types";

export const useDueDateTypesApi = () => {
  const api = useApi();

  const getAdminDueDateType = useCallback(
    (dueDateTypeId: string, signal: AbortSignal) => {
      const url = `/admin/due_date_types/${dueDateTypeId}`;
      return useSWRMutation<DueDateType | null, CustomError, string>(url, (url) =>
        api.get<DueDateType>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminDueDateTypes = useCallback(
    <K extends keyof DueDateType>(query: PaginationQueryWithFields<Pick<DueDateType, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<DueDateType, K>>("/admin/due_date_types", query);
      return useSWR<Paginated<Pick<DueDateType, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Pick<DueDateType, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminDueDateType = useCallback(
    async (body: UpsertDueDateType): Promise<{ id: string }> => {
      const url = `/admin/due_date_types`;
      const id = await api.post<UpsertDueDateType, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminDueDateType = useCallback(
    async (dueDateTypeId: string, body: UpsertDueDateType): Promise<void> => {
      const url = `/admin/due_date_types/${dueDateTypeId}`;
      (await api.put<UpsertDueDateType>(url, body),
        api.mutateCache(["/admin/due_date_types", `/admin/due_date_types/${dueDateTypeId}/versions`]));
    },
    [api],
  );

  const deleteAdminDueDateType = useCallback(
    async (dueDateTypeId: string): Promise<void> => {
      const url = `/admin/due_date_types/${dueDateTypeId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/due_date_types`]);
      });
    },
    [api],
  );

  const getAdminDueDateTypeHistory = useCallback(
    (query: PaginationQuery, dueDateTypeID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/due_date_types/${dueDateTypeID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminDueDateTypeVersion = useCallback(
    (dueDateTypeId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/due_date_types/${dueDateTypeId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<DueDateTypeVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<DueDateTypeVersion>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminDueDateType,
    updateAdminDueDateType,
    insertAdminDueDateType,
    useAdminDueDateTypes,
    getAdminDueDateType,
    getAdminDueDateTypeHistory,
    getAdminDueDateTypeVersion,
  };
};
